import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { 
    TrendingUp,
    GraduationCap, Briefcase, Video, Baby, Wand2, Brain, Megaphone, ArrowRight
} from 'lucide-react';

type StepImage =
  | string
  | {
      webpSrcSet: string;
      jpgSrcSet: string;
      fallback: string;
      width: number;
      height: number;
    };

const stepImageSizes = '(min-width: 1024px) 50vw, 100vw';

const steps: Array<{
  id: number;
  num: string;
  title: string;
  description: string;
  image: StepImage;
}> = [
    {
      id: 1,
      num: '01',
      title: 'Create an account',
      description: 'Create your account and choose the learning plan that best fits your goals.',
      image: {
        webpSrcSet: '/hands-laptop-keyboard-800.webp 800w, /hands-laptop-keyboard-1200.webp 1200w',
        jpgSrcSet: '/hands-laptop-keyboard-800.jpg 800w, /hands-laptop-keyboard-1200.jpg 1200w',
        fallback: '/hands-laptop-keyboard.jpg',
        width: 1200,
        height: 800,
      },
    },
    {
      id: 2,
      num: '02',
      title: 'Start Learning',
      description: 'Explore structured AI courses, practical lessons and supporting learning resources at your own pace.',
      image: {
        webpSrcSet: '/library-800.webp 800w, /library-1200.webp 1200w',
        jpgSrcSet: '/library-800.jpg 800w, /library-1200.jpg 1200w',
        fallback: '/library.jpg',
        width: 1200,
        height: 686,
      },
    },
    {
      id: 3,
      num: '03',
      title: 'Join Live Classes',
      description: 'View your eligible upcoming classes, join at the scheduled time and learn directly from an instructor.',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80'
    }
  ];

const AILearningHome = () => {
    const [activeStep, setActiveStep] = useState(1);
  const actionColorScheme = 'bg-zinc-950 border border-zinc-950 text-white hover:bg-white hover:text-zinc-950 hover:border-zinc-950';

    return (
        <>
            <Header />

        <main className="font-sans text-zinc-900 ">

                {/* ========================================
                    1. HERO SECTION (Business Scaling Focus)
                    ======================================== */}
 <section className="relative py-12 md:py-12 overflow-hidden border-b border-zinc-200">
  <div
    className="absolute inset-0 opacity-20"
    style={{
      backgroundImage:
        "linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)",
      backgroundSize: "40px 40px",
    }}
    ></div>

  <div className="absolute inset-0 "></div>

  <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[#f59e0b]/10 rounded-full blur-[120px] pointer-events-none z-0"></div>

  <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 items-center relative z-20 sm:w-fit ">
    {/* LEFT: Scaling & Skill Acquisition */}
    <div className="relative z-30 md:w-[650px] sm:w-fit">
      

      <h1 className="sm:w-auto text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-6 text-zinc-950 tracking-tighter md:w-[650px]">
        <span className="hero-headline block md:whitespace-nowrap md:text-5xl lg:text-6xl">Build Practical AI Skills</span>
        <span className="hero-headline block md:whitespace-nowrap md:text-5xl lg:text-6xl text-transparent bg-clip-text bg-[#f59e0b]">
          On Demand and Live.
        </span>
      </h1>

      <p className="max-w-2xl text-lg font-medium leading-relaxed text-zinc-600">
        Learn through practical, self-paced AI courses and scheduled instructor-led live classes with direct interaction, demonstrations and guidance.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 mt-10">
        <Link
          to="/library/"
          className={`${actionColorScheme} px-8 py-4 rounded-xl font-bold text-lg text-center transition-all shadow-xl shadow-zinc-200`}
        >
          Explore Courses & Live Classes
        </Link>
           
      </div>
    </div>

    {/* RIGHT: Simple Picture */}
    <div className="relative w-full h-[550px] md:h-[440px] lg:h-[550px] flex items-center justify-center lg:justify-end h-fit">
      {/* Here is the simple picture wrapper. 
         Aspect-square keeps it neat, max-w limits width on large screens.
         object-cover ensures the image fills the area.
      */}
      <div className="w-full h-fit max-w-[500px] md:max-w-[420px] lg:max-w-[500px] aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-[#f59e0b] border-4 border-white z-10">
        <img
          src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=800&auto=format&fit=crop"
          alt="Business team collaborating on AI scaling strategy"
          width="800"
          height="800"
          fetchPriority="high"
          loading="eager"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  </div>
</section>
             



{/* ========================================
    2. HOW IT WORKS: The Scaling Protocol (Light Mode - High Contrast Hover)
    ======================================== */}
 {/* ========================================
                    2. HOW IT WORKS: The Scaling Protocol (Restored Sticky Layout)
                    ======================================== */}
                <section className="bg-zinc-950 py-24 md:py-32 relative">
                    <div className="absolute top-0 right-1/4 w-[600px] h-[300px] bg-amber-500/5 rounded-full blur-[120px] pointer-events-none"></div>

                    <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10 flex flex-col lg:flex-row gap-16 items-start">
                        
                        {/* LEFT COLUMN: Sticky */}
                        <div className="w-full lg:w-1/3 lg:sticky lg:top-8">
                            <div className="pt-4">
                               
                                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tighter leading-tight">
                                    Your 3-Step <br className="hidden lg:block"/> Scaling Plan
                                </h2>
                                <p className="text-zinc-300 text-lg leading-relaxed mb-8 max-w-sm">
                                    Develop practical AI skills through structured courses, guided learning resources and interactive live classes.
                                </p>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Steps Cards */}
                        <div className="w-full lg:w-2/3 flex flex-col gap-8">
                            {/* Step 1 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-[#f59e0b] transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-zinc-800/10 group-hover:text-[#f59e0b29] transition-colors leading-none">01</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <GraduationCap className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Acquire Core Skills</h3>
                                    <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Build a strong foundation through practical, self-paced courses covering essential AI tools, concepts and workflows.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>

                            {/* Step 2 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-[#f59e0b] transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-[f59e0b29] group-hover:text-[#f59e0b29] transition-colors leading-none">02</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <Briefcase className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Apply Your Knowledge</h3>
                                    <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Turn what you learn into practical business applications through guided exercises, demonstrations and real-world examples.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>

                            {/* Step 3 */}
                            <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-8 md:p-12 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                                <div className="absolute -right-4 -bottom-10 text-[150px] font-black text-zinc-800/10 group-hover:text-[#f59e0b29] transition-colors leading-none">03</div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8  transition-all">
                                        <TrendingUp className="w-8 h-8 text-[#f59e0b]" />
                                    </div>
                                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Learn with Expert Guidance</h3>
                                    <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium">
                                        Join scheduled instructor-led live classes, ask questions and receive direct guidance to strengthen your understanding.
                                    </p>
                                </div>
                                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#f59e0b] group-hover:h-1/2 transition-all duration-500 rounded-r-full"></div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ========================================
                    3. ABOUT US: The Mission
                    ======================================== */}
                <section className="py-24 bg-white overflow-hidden border-t border-zinc-100">
                    <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
                        <div className="order-2 lg:order-1">
                            <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 text-xs font-bold text-zinc-600 mb-6 uppercase tracking-widest shadow-sm text-center">The Business Mission</span>
                            <h2 className="text-4xl md:text-5xl font-black text-zinc-900 mb-6 leading-tight tracking-tighter">
                                Bridging the Gap Between <br/> Complexity & Profit.
                            </h2>
                            <p className="text-lg text-zinc-600 mb-6 font-medium leading-relaxed">
                                LearnAI Hub was created for entrepreneurs and professionals who don’t have time for theory. We combine practical, self-paced AI courses with instructor-led live learning opportunities designed for real-world business application.
                            </p>
                           
                        </div>
                        <div className="relative order-1 lg:order-2">
                            <div className="absolute -top-6 -right-6 w-64 h-64" style={{ backgroundImage: 'radial-gradient(#d4d4d8 2px, transparent 2px)', backgroundSize: '20px 20px' }}></div>
                            <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Leadership" width="1000" height="667" className="relative z-10 rounded-3xl shadow-2xl shadow-[#f59e0b] w-full h-[450px] md:h-[360px] lg:h-[450px] object-cover border-4 border-zinc-100" />
                        </div>
                    </div>
                </section>


                {/* ========================================
                    3. Courses & Live Learning
                    ======================================== */}
                <section className="py-24 bg-zinc-950 overflow-hidden">
                  <div className="max-w-7xl mx-auto px-6 lg:px-12">

                    <div className="mb-16 md:mb-24 text-center">
                        <span className="inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 text-xs font-bold text-zinc-600 mb-6 uppercase tracking-widest shadow-sm text-center">Courses & Live Learning</span>
                        <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight tracking-tighter">
                            Learn AI Through <span className="text-transparent bg-clip-text bg-[#f59e0b]">Courses and Live Guidance</span>
                        </h2>
                        <p className="w-full max-w-3xl mx-auto text-lg text-zinc-300 mb-6 font-medium leading-relaxed">
                            Explore practical, beginner-friendly AI courses and strengthen your knowledge through scheduled live classes, demonstrations and instructor interaction.
                        </p>                        
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4 lg:gap-6 md:mb-6 mb-4">
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Video className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:min-h-[64px] lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">Viral Quiz Videos for TikTok & Shorts</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Create quiz videos using Gemini, ElevenLabs and AI image tools.
                        </p>
                        <Link to="/library/" aria-label="Open Viral Quiz Videos course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Baby className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:min-h-[64px] lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">Children’s Story Videos with AI</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Create stories, visuals, characters and voiceovers with AI.
                        </p>
                        <Link to="/library/" aria-label="Open Children's Story Videos course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Wand2 className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:min-h-[64px] lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">Fantasy Niche Videos with AI</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Produce fantasy-themed videos using AI-generated scripts and visuals.
                        </p>
                        <Link to="/library/" aria-label="Open Fantasy Niche Videos course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 mb-12">
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Brain className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">AI for Productivity</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Use AI to improve research, writing, planning and daily workflows.
                        </p>
                        <Link to="/library/" aria-label="Open AI for Productivity course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                      <div className="relative bg-[#121214] border border-zinc-800/80 rounded-[2rem] p-6 md:p-6 overflow-hidden group hover:border-amber-500/30 transition-all shadow-xl">
                        <div className="w-16 h-16 bg-zinc-900 border border-zinc-700 rounded-2xl flex items-center justify-center mb-8 transition-all">
                            <Megaphone className="w-8 h-8 text-[#f59e0b]" />
                        </div>
                        <h3 className="lg:text-2xl md:text-[18px] text-2xl font-bold text-white mb-4">AI in Digital Marketing</h3>
                        <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-lg font-medium mb-4">
                            Learn how AI can support content, campaigns and audience research.
                        </p>
                        <Link to="/library/" aria-label="Open AI in Digital Marketing course" className="h-10 w-10 rounded-full ml-auto flex items-center justify-center bg-zinc-950 cursor-pointer">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-[#fff]" height="20" width="20" id="fi_3114931" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m22.707 11.293-7-7a1 1 0 0 0 -1.414 1.414l5.293 5.293h-17.586a1 1 0 0 0 0 2h17.586l-5.293 5.293a1 1 0 1 0 1.414 1.414l7-7a1 1 0 0 0 0-1.414z"></path>
                          </svg>
                        </Link>
                      </div>
                    </div>

                    <section className="my-12 rounded-[2rem] border border-amber-500/30 bg-[#121214] p-8 md:p-12 shadow-xl">
                      <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#f59e0b]">
                        Instructor-Led Learning
                      </span>
                      <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
                        <div>
                          <h3 className="text-3xl font-black tracking-tighter text-white md:text-4xl">Join Scheduled Live AI Classes</h3>
                          <p className="mt-5 max-w-2xl text-base font-medium leading-relaxed text-zinc-300 md:text-lg">
                            Learn directly from an instructor through scheduled online classes. Follow practical demonstrations, ask questions during the session and receive guidance that helps you apply AI with confidence.
                          </p>
                        </div>
                        <ul className="space-y-3 text-base font-medium text-zinc-200">
                          <li className="flex gap-3"><span className="text-[#f59e0b]" aria-hidden="true">•</span>Scheduled online teaching sessions</li>
                          <li className="flex gap-3"><span className="text-[#f59e0b]" aria-hidden="true">•</span>Direct instructor interaction</li>
                          <li className="flex gap-3"><span className="text-[#f59e0b]" aria-hidden="true">•</span>Practical demonstrations and guided learning</li>
                          <li className="flex gap-3"><span className="text-[#f59e0b]" aria-hidden="true">•</span>Opportunities to ask questions</li>
                          <li className="flex gap-3"><span className="text-[#f59e0b]" aria-hidden="true">•</span>Available classes shown through your account</li>
                        </ul>
                      </div>
                      <Link
                        to="/pricing/"
                        className="mt-8 inline-flex rounded-xl border border-white bg-white px-6 py-3 font-bold text-zinc-950 transition-all hover:bg-transparent hover:text-white"
                      >
                        View Learning Plans
                      </Link>
                    </section>

                    {/* five-icon-grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                      <div className="flex items-start gap-4">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-zinc-950" height="20" width="20" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" id="fi_2783301"><g id="Layer_2" data-name="Layer 2">
                            <path d="M27.37,2.65H4.63A3.64,3.64,0,0,0,1,6.28V20.8a3.64,3.64,0,0,0,3.63,3.63H15v1.88l-3,0a1,1,0,0,0-1,1,1,1,0,0,0,1,1l8-.09a1,1,0,1,0,0-2l-3,0V24.43H27.37A3.64,3.64,0,0,0,31,20.8V6.28A3.64,3.64,0,0,0,27.37,2.65ZM29,20.8a1.63,1.63,0,0,1-1.63,1.63H4.63A1.63,1.63,0,0,1,3,20.8V6.28A1.63,1.63,0,0,1,4.63,4.65H27.37A1.63,1.63,0,0,1,29,6.28Z"></path></g>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">FORMAT</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Self-paced courses and instructor-led live classes
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg id="fi_2889676"className="lucide lucide-trending-up w-4 h-4 text-zinc-950" height="16" width="16" viewBox="0 0 35 35" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><g>
                            <path d="m17 1c-5 0-9 4-9 9v4c-1.7 0-3 1.3-3 3v13c0 1.7 1.3 3 3 3h18c1.7 0 3-1.3 3-3v-13c0-1.7-1.3-3-3-3v-4c0-5-4-9-9-9zm10 16v13c0 .6-.4 1-1 1h-18c-.6 0-1-.4-1-1v-13c0-.6.4-1 1-1h1 16 1c.6 0 1 .4 1 1zm-17-3v-4c0-3.9 3.1-7 7-7s7 3.1 7 7v4z"></path>
                            <path d="m17 19c-1.7 0-3 1.3-3 3 0 1.3.8 2.4 2 2.8v2.2c0 .6.4 1 1 1s1-.4 1-1v-2.2c1.2-.4 2-1.5 2-2.8 0-1.7-1.3-3-3-3zm0 4c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"></path></g>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">ACCESS</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Courses, resources and eligible live classes through your account
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4 lg:ml-8 ml-0">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-zinc-950" height="20" width="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" id="fi_9308008"><g fill="rgb(0,0,0)">
                            <path d="m12 12.75c-3.17 0-5.75-2.58-5.75-5.75s2.58-5.75 5.75-5.75 5.75 2.58 5.75 5.75-2.58 5.75-5.75 5.75zm0-10c-2.34 0-4.25 1.91-4.25 4.25s1.91 4.25 4.25 4.25 4.25-1.91 4.25-4.25-1.91-4.25-4.25-4.25z"></path>
                            <path d="m20.5901 22.75c-.41 0-.75-.34-.75-.75 0-3.45-3.5199-6.25-7.8399-6.25-4.32005 0-7.84004 2.8-7.84004 6.25 0 .41-.34.75-.75.75s-.75-.34-.75-.75c0-4.27 4.18999-7.75 9.34004-7.75 5.15 0 9.3399 3.48 9.3399 7.75 0 .41-.34.75-.75.75z"></path></g>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">SKILL LEVEL</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Beginner-friendly
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="min-h-9 min-w-9 rounded-[8px] flex items-center justify-center bg-[#f59e0b]">
                          <svg className="lucide lucide-trending-up w-4 h-4 text-zinc-950" height="20" width="20" viewBox="0 0 44 44" xmlns="http://www.w3.org/2000/svg" data-name="Layer 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" id="fi_3119243"><g id="Education">
                            <path d="M24,24a1.0067,1.0067,0,0,1-.356-.0654l-21-8a1,1,0,0,1,0-1.8692l21-8a1.0049,1.0049,0,0,1,.712,0l21,8a1,1,0,0,1,0,1.8692l-21,8A1.0067,1.0067,0,0,1,24,24ZM5.8091,15,24,21.93,42.1909,15,24,8.07Z"></path>
                            <path d="M24,38a1.0067,1.0067,0,0,1-.356-.0654l-13-4.95A1,1,0,0,1,10,32.05v-14a1,1,0,0,1,2,0V31.36l12,4.57,12-4.57V18.05a1,1,0,0,1,2,0v14a1,1,0,0,1-.644.9346l-13,4.95A1.0067,1.0067,0,0,1,24,38Z"></path>
                            <path d="M7,36a1,1,0,0,1-1-1V16.5239a1,1,0,0,1,.9106-.9961l17-1.5239a1,1,0,0,1,.1788,1.9922L8,17.4385V35A1,1,0,0,1,7,36Z"></path>
                            <path d="M9,42H5a1,1,0,0,1-1-1V37a3,3,0,0,1,6,0v4A1,1,0,0,1,9,42ZM6,40H8V37a1,1,0,0,0-2,0Z"></path></g>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-medium font-bold text-white mb-1">LEARNING STYLE</h3>
                          <p className="text-zinc-300 text-[14px] md:text-normal lh-[13px]">
                              Flexible, practical and instructor-supported
                          </p>
                        </div>
                      </div>
                      {/* 5th column commented out */}
                    </div>

                    <div className="flex justify-center flex-col sm:flex-row gap-4 mt-10">
                      <Link
                        to="/library/"
                        className={`bg-white border border-zinc-200 px-8 py-4 rounded-xl font-bold text-lg text-center transition-all hover:bg-transparent hover:text-white`}
                      >
                        Enroll Now
                      </Link>                        
                    </div>

                  </div>
                </section>


                {/* ========================================
                    Get started
                    ======================================== */}

<section className="bg-white py-24 md:py-32 border-y border-zinc-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        
        {/* Top Header */}
        <div className="mb-16 md:mb-24 text-center ">
          <div className="mx-auto inline-flex items-center rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 text-xs font-bold text-zinc-600 mb-6 uppercase tracking-widest shadow-sm text-center">
            Get started
          </div>
       
        </div>

        {/* Main Interactive Section */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
          
          {/* Left Side: Dynamic Image Gallery */}
          <div className="w-full lg:w-1/2 relative h-[400px] md:h-[420px] lg:h-[650px] rounded-[2.5rem] overflow-hidden bg-zinc-100 shadow-2xl shadow-[#f59e0b]">
            {steps.map((step) => {
              const isActive = activeStep === step.id;
              const imageClasses = `absolute inset-0 w-full h-full object-cover transition-all duration-1000 ease-in-out ${
                isActive
                  ? 'opacity-100 scale-100'
                  : 'opacity-0 scale-105 pointer-events-none'
              }`;

              if (typeof step.image === 'string') {
                return (
                  <img
                    key={step.id}
                    src={step.image}
                    alt={step.title}
                    width="1200"
                    height="800"
                    loading={step.id === 1 ? 'eager' : 'lazy'}
                    className={imageClasses}
                  />
                );
              }

              return (
                <picture key={step.id} className="absolute inset-0">
                  <source type="image/webp" srcSet={step.image.webpSrcSet} sizes={stepImageSizes} />
                  <source type="image/jpeg" srcSet={step.image.jpgSrcSet} sizes={stepImageSizes} />
                  <img
                    src={step.image.fallback}
                    alt={step.title}
                    width={step.image.width}
                    height={step.image.height}
                    loading={step.id === 1 ? 'eager' : 'lazy'}
                    className={imageClasses}
                  />
                </picture>
              );
            })}
            {/* Subtle inner shadow overlay */}
            <div className="absolute inset-0 border border-black/5 rounded-[2.5rem] z-10 pointer-events-none"></div>
          </div>

          {/* Right Side: Expandable Accordion List */}
          <div className="w-full lg:w-1/2 flex flex-col justify-center">
            {steps.map((step) => {
              const isActive = activeStep === step.id;

              return (
                <button
                  type="button"
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`group w-full cursor-pointer border-b border-zinc-200 py-8 text-left transition-all duration-300 ${
                    isActive ? 'opacity-100' : 'opacity-80 hover:opacity-100'
                  }`}
                  aria-expanded={isActive}
                  aria-controls={`home-step-${step.id}`}
                >
                  {/* Step Header (Always Visible) */}
                  <div className="flex items-center gap-6">
                    <span className={`text-4xl md:text-5xl font-black transition-colors duration-300 ${
                      isActive ? 'text-zinc-900' : 'text-zinc-700 group-hover:text-zinc-800'
                    }`}>
                      {step.num}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight flex-1">
                      {step.title}
                    </h3>
                    
                    {/* Animated Arrow Icon */}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 text-lg shadow-xl shadow-zinc-200 ${actionColorScheme} ${isActive ? '' : 'opacity-80'}`}
                    >
                      <ArrowRight className={`w-5 h-5`} />
                    </div>
                  </div>

                  {/* Expandable Description (Uses CSS Grid for smooth height transition) */}
                    <div
                      id={`home-step-${step.id}`}
                    className={`grid transition-all duration-500 ease-in-out ${
                      isActive ? 'grid-rows-[1fr] opacity-100 mt-6' : 'grid-rows-[0fr] opacity-0'
                    }`}
                  >
                    <div className="overflow-hidden pl-14 md:pl-20 pr-4">
                      <p className="text-zinc-700 text-lg leading-relaxed font-medium">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      </div>
    </section>  

  
            </main>
            <Footer />
        </>
    );
};

export default AILearningHome;
